import { useState } from "react"
import { useTranslation } from "react-i18next"
import { CalendarRangeIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { formatDate, type SupportedLocale } from "@/lib/datetime"

export type NamedRange = "7d" | "30d" | "90d"
/** A named window up to now, or two days picked (`yyyy-MM-dd`, both included). */
export type RangeValue = { range: NamedRange } | { range: "custom"; from: string; to: string }

export const NAMED_RANGES: readonly NamedRange[] = ["7d", "30d", "90d"]
/** The backend refuses a longer custom range (ops `maxCustomRange`). */
export const MAX_CUSTOM_DAYS = 366
const DAY = 86_400_000

const dayOf = (d: string): number | undefined => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return undefined
  const t = new Date(`${d}T00:00`).getTime()
  return Number.isNaN(t) ? undefined : t
}

/** Why two picked days are not a range, or `undefined` when they are. */
export function customRangeProblem(from: string, to: string): "order" | "tooLong" | undefined {
  const a = dayOf(from)
  const b = dayOf(to)
  if (a === undefined || b === undefined || b < a) return "order"
  // `to` is a whole day: the window ends the day after it.
  return b + DAY - a > MAX_CUSTOM_DAYS * DAY ? "tooLong" : undefined
}

const DEFAULT_RANGE: RangeValue = { range: "30d" }

/** The range the URL asks for (`range`, `from`, `to`); anything unusable falls back to 30 days. */
export function rangeFromUrl(q: { range: string; from: string; to: string }): RangeValue {
  if (q.range === "custom") {
    return customRangeProblem(q.from, q.to)
      ? DEFAULT_RANGE
      : { range: "custom", from: q.from, to: q.to }
  }
  return (NAMED_RANGES as readonly string[]).includes(q.range)
    ? { range: q.range as NamedRange }
    : DEFAULT_RANGE
}

/** A range as the API's `?range&from&to`: two picked days become local-midnight edges, `to` exclusive. */
export function rangeParams(r: RangeValue): {
  range: RangeValue["range"]
  from?: string
  to?: string
} {
  if (r.range !== "custom") return { range: r.range }
  const to = new Date(`${r.to}T00:00`)
  to.setDate(to.getDate() + 1)
  return { range: "custom", from: new Date(`${r.from}T00:00`).toISOString(), to: to.toISOString() }
}

/**
 * `7d`/`30d`/`90d`/custom (LLD §8.1). The caller keeps the value in the URL;
 * this only renders it. Custom takes two days in native date inputs, at most
 * 366 days apart.
 */
export function RangePicker({
  value,
  onChange,
  locale,
}: {
  value: RangeValue
  onChange: (next: RangeValue) => void
  locale: SupportedLocale
}) {
  const { t } = useTranslation("common")
  const [open, setOpen] = useState(false)
  const [from, setFrom] = useState(value.range === "custom" ? value.from : "")
  const [to, setTo] = useState(value.range === "custom" ? value.to : "")
  const problem = from && to ? customRangeProblem(from, to) : undefined

  return (
    <div
      role="group"
      aria-label={t("range.label")}
      className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-md border border-border bg-card p-1"
    >
      {NAMED_RANGES.map((r) => (
        <Button
          key={r}
          type="button"
          size="sm"
          variant={value.range === r ? "default" : "ghost"}
          aria-pressed={value.range === r}
          onClick={() => {
            onChange({ range: r })
          }}
        >
          {t(`range.${r}`)}
        </Button>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant={value.range === "custom" ? "default" : "ghost"}
            aria-pressed={value.range === "custom"}
          >
            <CalendarRangeIcon aria-hidden />
            {value.range === "custom" ? (
              <span className="numeric">
                {formatDate(`${value.from}T00:00`, locale)} –{" "}
                {formatDate(`${value.to}T00:00`, locale)}
              </span>
            ) : (
              t("range.custom")
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 space-y-3" aria-label={t("range.dialog")}>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault()
              if (!from || !to || problem) return
              onChange({ range: "custom", from, to })
              setOpen(false)
            }}
          >
            <div className="grid gap-1.5">
              <Label htmlFor="range-from">{t("range.from")}</Label>
              <Input
                id="range-from"
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value)
                }}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="range-to">{t("range.to")}</Label>
              <Input
                id="range-to"
                type="date"
                value={to}
                aria-invalid={problem ? true : undefined}
                aria-describedby={problem ? "range-problem" : undefined}
                onChange={(e) => {
                  setTo(e.target.value)
                }}
              />
            </div>
            {problem ? (
              <p id="range-problem" className="text-sm text-destructive">
                {t(`range.${problem}`, { days: MAX_CUSTOM_DAYS })}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setOpen(false)
                }}
              >
                {t("actions.cancel")}
              </Button>
              <Button type="submit" size="sm" disabled={!from || !to || problem !== undefined}>
                {t("range.apply")}
              </Button>
            </div>
          </form>
        </PopoverContent>
      </Popover>
    </div>
  )
}
